import type { Country } from "@sveabank/cc-common";
import { onboardingApi } from ".";
import type {
	Application,
	ApplicationDetails,
	ApplicationStatus,
	Engagement,
	FullAccessUser,
	FullApplicationData,
	Promotion,
} from "../types/application";
import type { ContactInformationPost } from "../types/contact-information";
import type { GuarantorName } from "../types/guarantor";
import type { CardDisplayName, CardSettingsPost, CreditProductConfig } from "../types/product";
import type { SignatorEmailAssignPost } from "../types/signing";
import type { Step } from "../types/step";
import {
	assignFullAccessUsers,
	assignSignatorEmails,
	configureCheckCredit,
	configureCreditProducts,
	configureMastercard,
	creditScore,
	deleteApplicationMutation,
	fetchApplicationDetails,
	fetchApplicationRevision,
	fetchApplicationStatusQuery,
	fetchApplicationsQuery,
	fetchCardDisplayNames,
	fetchCountries,
	fetchEngagementsQuery,
	fetchFullAccessUsers,
	fetchPersonInfo,
	fetchPromotionsQuery,
	fetchSteps,
	finalizeApplication,
	initApplicationMutation,
	registerApplicantContactInfo,
	registerOrganizationContactInfo,
	removeFullAccessUsers,
} from "./endpoints/application";

export const applicationsApiSlice = onboardingApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchEngagements: builder.query<Engagement[], void>({
			query: () => fetchEngagementsQuery(),
			transformResponse: (response: { organisations: Engagement[] }) => response.organisations,
			extraOptions: { withAuth: true },
		}),
		fetchPersonInfo: builder.query<GuarantorName, string>({
			query: (applicationId) => fetchPersonInfo(applicationId),
			transformResponse: (response: { data: GuarantorName }) => response.data,
			extraOptions: { withAuth: true },
		}),
		fetchApplications: builder.query<Application[], void>({
			query: () => fetchApplicationsQuery(),
			transformResponse: (response: { data: Application[] }) =>
				response.data.filter(
					(application) => application.status !== "Finalized" && application.status !== "Cancelled",
				),
			extraOptions: { withAuth: true },
		}),

		fetchPromotions: builder.query<
			{ promotionStarts: string; promotionEnds: string; promotion: Promotion }[],
			void
		>({
			query: () => fetchPromotionsQuery(),
			transformResponse: (response: {
				promotions: { promotionStarts: string; promotionEnds: string; promotion: Promotion }[];
			}) => response.promotions,
			extraOptions: { withAuth: true },
		}),

		fetchApplicationStatus: builder.query<ApplicationStatus, string>({
			providesTags: ["CurrentApplication"],
			query: (applicationId) => fetchApplicationStatusQuery(applicationId),
			transformResponse: (res: { status: ApplicationStatus }) => res.status,
			extraOptions: { withAuth: true },
		}),
		initApplication: builder.mutation<
			{ revision?: string; applicationId: string },
			{ orgNr: string; applicationId: string; promotionCodes?: string[] }
		>({
			query: ({ orgNr, applicationId, promotionCodes }) =>
				initApplicationMutation(orgNr, applicationId, promotionCodes),
			invalidatesTags: ["DetailedApplications", "CurrentApplication"],
			transformResponse: (_returnValue, _meta, arg) => ({
				applicationId: arg.applicationId,
			}),
			extraOptions: { withAuth: true },
		}),

		deleteApplication: builder.mutation<void, string>({
			invalidatesTags: [
				"DetailedApplications",
				"AssignedGuarantors",
				"CurrentApplication",
				"BeneficialOwners",
				"FullAccessUsers",
			],
			query: (applicationId) => deleteApplicationMutation(applicationId),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),

		fetchApplicationRevision: builder.query<void, string>({
			query: (applicationId) => fetchApplicationRevision(applicationId),
			forceRefetch: () => true,
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
		finalizeApplication: builder.mutation<void, string>({
			invalidatesTags: ["CurrentApplication", "DetailedApplications"],
			query: (applicationId) => finalizeApplication(applicationId),
			extraOptions: {
				revision: true,
				allowRevisionConflict: false,
				withAuth: true,
			},
		}),
		fetchSteps: builder.query<Step[], string>({
			query: (applicationId) => fetchSteps(applicationId),
			providesTags: ["CurrentApplication"],
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: Step[] }) => response.data,
		}),

		creditScore: builder.mutation<
			void,
			{
				applicationId: string;
				orgNr: string;
			}
		>({
			query: ({ applicationId, orgNr }) => creditScore(applicationId, orgNr),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			invalidatesTags: ["CurrentApplication"],
		}),
		configureCreditProducts: builder.mutation<
			void,
			{ applicationId: string; config: CreditProductConfig[] }
		>({
			query: ({ applicationId, config }) => configureCreditProducts(applicationId, config),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			invalidatesTags: ["CurrentApplication"],
		}),
		configureCheckCredit: builder.mutation<
			void,
			{
				applicationId: string;
				config: { accountId: string; productId: string };
			}
		>({
			query: ({ applicationId, config }) => configureCheckCredit(applicationId, config),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			invalidatesTags: ["CurrentApplication"],
		}),
		registerOrganizationContactInfo: builder.mutation<
			void,
			{
				applicationId: string;
				contactInfo: ContactInformationPost;
			}
		>({
			query: ({ applicationId, contactInfo }) =>
				registerOrganizationContactInfo(applicationId, contactInfo),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
		registerApplicantContactInfo: builder.mutation<
			void,
			{
				applicationId: string;
				contactInfo: ContactInformationPost;
			}
		>({
			query: ({ applicationId, contactInfo }) =>
				registerApplicantContactInfo(applicationId, contactInfo),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),

		fetchDetailedApplications: builder.query<Array<FullApplicationData>, void>({
			async queryFn(_, queryApi, _extra, fetchWithBaseQuery) {
				try {
					const applications: Array<Application> = await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchApplications.initiate(undefined, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return {
						data: await Promise.all(
							applications.map(async (application): Promise<FullApplicationData> => {
								const [stepsResult, detailsResult] = await Promise.all([
									fetchWithBaseQuery(fetchSteps(application.applicationId)),
									fetchWithBaseQuery(fetchApplicationDetails(application.applicationId)),
								]);

								const error = stepsResult.error || detailsResult.error;
								if (error) throw new Error(error.status.toString());

								const steps = stepsResult.data as {
									data: Array<Step>;
								};
								const details = detailsResult.data as ApplicationDetails;

								return {
									...application,
									steps: steps.data,
									details,
								};
							}),
						),
					};
				} catch (error: any) {
					return {
						error: {
							error: error?.message,
							status: "FETCH_ERROR",
						},
					};
				}
			},
			extraOptions: { withAuth: true },
			providesTags: ["DetailedApplications"],
		}),

		concludeContactInfo: builder.mutation<
			null,
			{
				applicantContactInfo: ContactInformationPost;
				organizationContactInfo: ContactInformationPost;
				applicationId: string;
			}
		>({
			async queryFn({ applicantContactInfo, organizationContactInfo, applicationId }, queryApi) {
				try {
					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.registerApplicantContactInfo.initiate({
								applicationId,
								contactInfo: applicantContactInfo,
							}),
						)
						.unwrap();

					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.registerOrganizationContactInfo.initiate({
								applicationId,
								contactInfo: organizationContactInfo,
							}),
						)
						.unwrap();

					await queryApi.dispatch(
						applicationsApiSlice.endpoints.fetchApplicationDetails.initiate(applicationId, {
							forceRefetch: true,
						}),
					);
					await queryApi
						.dispatch(
							applicationsApiSlice.endpoints.fetchSteps.initiate(applicationId, {
								forceRefetch: true,
							}),
						)
						.unwrap();

					return { data: null };
				} catch (error) {
					return {
						error: {
							error: error as string,
							status: "CUSTOM_ERROR",
						},
					};
				}
			},
		}),

		fetchApplicationDetails: builder.query<ApplicationDetails, string>({
			query: (applicationId) => fetchApplicationDetails(applicationId),
			providesTags: ["CurrentApplication"],
			extraOptions: { withAuth: true },
		}),
		assignSignatorEmails: builder.mutation<
			void,
			{
				applicationId: string;
				signatories: SignatorEmailAssignPost[];
			}
		>({
			query: ({ applicationId, signatories }) => assignSignatorEmails(applicationId, signatories),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
		fetchFullAccessUsers: builder.query<FullAccessUser[], string>({
			query: (applicationId) => fetchFullAccessUsers(applicationId),
			transformResponse: (response: { data: FullAccessUser[] }) => response.data,
			providesTags: ["FullAccessUsers"],
			extraOptions: { withAuth: true },
		}),
		assignFullAccessUsers: builder.mutation<
			null,
			{
				applicationId: string;
				fullAccessUsers: { nationalIds: string[] };
			}
		>({
			query: ({ applicationId, fullAccessUsers }) =>
				assignFullAccessUsers(applicationId, fullAccessUsers),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			invalidatesTags: ["FullAccessUsers", "CurrentApplication"],
		}),
		removeFullAccessUsers: builder.mutation<null, string>({
			query: (applicationId) => removeFullAccessUsers(applicationId),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			invalidatesTags: ["CurrentApplication"],
		}),
		fetchCardDisplayNames: builder.query<CardDisplayName[], string>({
			query: (ssn) => fetchCardDisplayNames(ssn),
			transformResponse: (response: { data: CardDisplayName[] }) => response.data,
			extraOptions: { withAuth: true },
		}),
		configureMastercard: builder.mutation<
			null,
			{
				applicationId: string;
				cardSettings: CardSettingsPost;
			}
		>({
			query: ({ applicationId, cardSettings }) => configureMastercard(applicationId, cardSettings),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
			invalidatesTags: ["CurrentApplication"],
		}),
		fetchCountries: builder.query<Country[], void>({
			query: () => fetchCountries(),
			transformResponse: (response: { data: Country[] }) => response.data,
			extraOptions: { withAuth: true },
		}),
	}),
	overrideExisting: false,
});

export const {
	useFetchEngagementsQuery,
	useFetchApplicationStatusQuery,
	useInitApplicationMutation,
	useDeleteApplicationMutation,
	useLazyFetchApplicationRevisionQuery,
	useFetchStepsQuery,
	useCreditScoreMutation,
	useFetchApplicationDetailsQuery,
	useConfigureCreditProductsMutation,
	useFetchFullAccessUsersQuery,
	useLazyFetchPersonInfoQuery,
	useAssignSignatorEmailsMutation,
	useLazyFetchCardDisplayNamesQuery,
	useConfigureMastercardMutation,
	useAssignFullAccessUsersMutation,
	useRemoveFullAccessUsersMutation,
	useConfigureCheckCreditMutation,
	useFetchCountriesQuery,
	useConcludeContactInfoMutation,
	useFinalizeApplicationMutation,
	useFetchDetailedApplicationsQuery,
	useFetchPromotionsQuery,
} = applicationsApiSlice;
